require('es6-promise').polyfill();
import type { AppDispatch, RootState } from '../store/configureStore';
import history from '../utils/history';
// Re-exported below so useActions()'s bindActionCreators keeps exposing them.
import { login, logout, logoutFederated } from './authThunks';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import omit from 'lodash/omit';
import { toastr } from '../utils/toastr';
// M6: auth is an RTK slice now — its action creators live with the reducer.
// (setSuperuserActive is aliased to avoid colliding with the thunk of the same name.)
import {
  requestUsers,
  requestUsersError,
  requestUsersSuccess,
} from '../reducers/users';
import {
  requestUser,
  requestUserError,
  requestUserSuccess,
  editUser as editUserAction,
  validateUser as validateUserAction,
  resetUser as resetUserAction,
  requestSaveUser,
  requestSaveUserSuccess,
  requestSaveUserError,
  requestDeleteUser,
  requestDeleteUserError,
  setSelectedRootTargets,
  setSelectedDashboards,
  requestSaveAuth0User,
  requestSaveAuth0UserSuccess,
  requestSaveAuth0UserError,
} from '../reducers/user';
import {
  requestUserTargets, requestUserTargetsError, requestUserTargetsSuccess,
  requestUserTargetsRoot, requestUserTargetsRootError, requestUserTargetsRootSuccess,
  requestUserDashboards, requestUserDashboardsError, requestUserDashboardsSuccess,
  requestUserCustomers, requestUserCustomersError, requestUserCustomersSuccess,
  requestUserCustomer, requestUserCustomerError, requestUserCustomerSuccess,
  requestEditUserCustomer,
  requestSaveUserCustomer, requestSaveUserCustomerSuccess, requestSaveUserCustomerError,
  requestDeleteCustomer, requestDeleteCustomerSuccess, requestDeleteCustomerError,
  requestValidateUserCustomer,
  resetCustomer as resetCustomerAction,
} from '../reducers/customer';
import {
  requestDashboardTargets, requestDashboardTargetsError, requestDashboardTargetsSuccess,
  requestDashboardRules, requestDashboardRulesError, requestDashboardRulesSuccess,
  setSelectedTargets,
  editDashboard as editDashboardAction,
  validateDashboard as validateDashboardAction,
  saveDashboard, saveDashboardError, saveDashboardSuccess,
  requestDashboard, requestDashboardError, requestDashboardSuccess,
  requestDeleteDashboard, requestDeleteDashboardSuccess, requestDeleteDashboardError,
  resetDashboard as resetDashboardAction,
} from '../reducers/dashboard';
import {
  setSuperuserActive as setSuperuserActiveAction,
} from '../reducers/auth';



import {
  SUPERUSER_ACTIVE_KEY,
  USER_TARGETS_KEY,
  ERROR_TOKEN_EXPIRED,
  TAPLAUSE_NEW_API_BASE_URL,
  PASSWORD_MIN_LENGTH,
} from '../constants/config';
import { handleErrors } from '../utils/http';
import {
  findNodeKeyPath,
  collectTreeNodes,
  prepareTreeState,
  prepareTargetForAPI,
  prepareDashboardForAPI
} from '../utils/helpers';
// Tree/target action-type constants now live in their own module so this file
// exports only functions (see src/constants/treeActions.ts). Imported here for the
// tree thunks' dispatch({ type: ... }) calls.
import {
  SELECT_NODE,
  DESELECT_NODE,
  TOGGLE_NODE,
  SHOW_DISABLED_TARGETS,
  EDIT_NODE,
  SET_CUSTOM_PICTURE,
  SET_TOGGLED,
  REQUEST_TREE,
  REQUEST_TARGET,
  REQUEST_TREE_ERROR,
  REQUEST_TARGET_ERROR,
  RECEIVE_TREE_SUCCESS,
  RECEIVE_TARGET_SUCCESS,
  PARSE_TARGETS,
  SAVE_TARGET,
  SAVE_TARGET_SUCCESS,
  SAVE_TARGET_ERROR,
  FILTER_TREE,
  RESET_TREE,
  REQUEST_DELETE_TARGET,
  REQUEST_DELETE_TARGET_SUCCESS,
  REQUEST_DELETE_TARGET_ERROR,
  REQUEST_DELETE_TARGET_FEEDBACK,
  REQUEST_DELETE_TARGET_FEEDBACK_SUCCESS,
  REQUEST_DELETE_TARGET_FEEDBACK_ERROR,
} from '../constants/treeActions';
const REQUEST_S3_SIGNATURE = 'REQUEST_S3_SIGNATURE';
const REQUEST_S3_SIGNATURE_ERROR = 'REQUEST_S3_SIGNATURE_ERROR';
const RECEIVE_S3_SIGNATURE_SUCCESS = 'RECEIVE_S3_SIGNATURE_SUCCESS';
const REQUEST_S3_UPLOAD = 'REQUEST_S3_UPLOAD';
const REQUEST_S3_UPLOAD_ERROR = 'REQUEST_S3_UPLOAD_ERROR';
const RECEIVE_S3_UPLOAD_SUCCESS = 'RECEIVE_S3_UPLOAD_SUCCESS';
const TREE_SORT_DEPTH = 5;

// Regexp for validating emails.
const MAIL_REGEXP = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
//
console.log('actionCreator - TAPLAUSE_NEW_API_BASE_URL:' + TAPLAUSE_NEW_API_BASE_URL);
const TAPLAUSE_NEW_API_ADMIN_BASE = TAPLAUSE_NEW_API_BASE_URL + '/admin';
/**
*
* @param {[object]} error [Exception error]
* @return {[boolean]}     [True if session is expired]
*/
const sessionExpired = (error: any) => {
  // Logout if "Bad HTTP authentication header format"
  if (error.status === 400) {
    console.debug('*** Session expired. Please login.');
    toastr.warning('Session expired', 'Please login again');
    return true;
  }
  // Logout if token was expired.
  if (error.status === 401 && error.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
    console.debug('*** Session expired. Please login.');
    toastr.warning('Session expired', 'Please login again');
    return true;
  }
  return false;
}
//-----------------------------------------------------
// Return token or null if expired
//-----------------------------------------------------
const getToken = () => {
  const auth0data = getAuth0Data();
  if (!auth0data) {
    return null;
  }

  const accessToken = auth0data.accessToken;
  const expiresAt = auth0data.expiresAt;

  if (!accessToken || !expiresAt || Date.now() > expiresAt) {
    console.log('*** Session expired');
    return null;
  } else {
    return accessToken;
  }
}

const getProfile = () => {
  const auth0data = getAuth0Data();
  if (!auth0data) {
    return null;
  }

  const profile = auth0data.profile;
  return profile;
}

const getAuth0Data = () => {
  const _auth0data = localStorage.getItem('auth0');
  if (!_auth0data) {
    console.log('*** No auth0 data');
    return null;
  }

  return JSON.parse(_auth0data);
}

/**
 *  Selects target node from the tree.
 *  @param  {[type]} node    [description]
 *  @param  {[type]} toggled [description]
 *  @return {[type]}         [description]
 */
const selectNode = (node: any, toggled: boolean, fetch = true) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(deselectNode(node._id));
    dispatch(toggleNode(node._id, toggled));

    if (fetch) {
      return dispatch(fetchTarget(node._id));
    }
  };
};

/**
 *  De-selects currently selected node from the tree.
 *  @param  {[type]} nodeId [description]
 *  @return {[type]}        [description]
 */
const deselectNode = (nodeId: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    // Get id of currently selected node.
    const currentNodeId = getState().tree.selectedNode._id;

    // If nodes are not currently selected, do nothing.
    if (!currentNodeId || currentNodeId === nodeId) {
      return;
    }

    // Find keyPath for currently selected node. Was
    // findKey(flattenObject(wholeTree)) — see findNodeKeyPath's note.
    const nodePath = findNodeKeyPath(getState().tree.tree, currentNodeId);
    if (!nodePath) {
      return;
    }
    const keyPath = ['tree', ...nodePath];

    // Set currently selected node in-active.
    const node = { ...get(getState().tree, keyPath), active: false };

    return dispatch({ type: DESELECT_NODE, payload: { keyPath, node } });
  };
};

/**
 *  Toggles selected node.
 *  @param  {[type]} nodeId  [description]
 *  @param  {[type]} toggled [description]
 *  @return {[type]}         [description]
 */
const toggleNode = (nodeId: string, toggled: boolean, active = true) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    // Find keyPath for toggled node. Was findKey(flattenObject(wholeTree)).
    const nodePath = findNodeKeyPath(getState().tree.tree, nodeId);
    if (!nodePath) {
      return;
    }
    const keyPath = ['tree', ...nodePath];

    // Set selected node active.
    const node = { ...get(getState().tree, keyPath), toggled: toggled, active: active };

    dispatch(setToggled(nodeId, toggled));
    return dispatch({ type: TOGGLE_NODE, payload: { keyPath, node } });
  };
};

/**
 *  [description]
 *  @param  {[type]} showDisabled [description]
 *  @return {[type]}              [description]
 */
const showDisabledTargets = (showDisabled: boolean) => {
  return (dispatch: AppDispatch) => dispatch({ type: SHOW_DISABLED_TARGETS, payload: showDisabled });
}

/**
 *  [Action from super/user toggle button. Reducer in auth.js]
 *  @param  {[boolean]} setSuperuserActive [True if superuser is active]
 *  @return {[boolean]}              [as above]
 */
const setSuperuserActive = (superuserIsActive: boolean) => {
  localStorage.setItem(SUPERUSER_ACTIVE_KEY, String(superuserIsActive));
  return (dispatch: AppDispatch) => dispatch(setSuperuserActiveAction({ isSuperuserActive: superuserIsActive }));
}

/**
 *  [description]
 *  @param  {[type]} parentId [description]
 *  @return {[type]}          [description]
 */
const changeParent = (parentId: string) => {
  return (dispatch: AppDispatch) => dispatch(editNode('parent', parentId));
};

/**
 *  Updates given property of selected node.
 *  @param  {[type]} property [description]
 *  @param  {[type]} value    [description]
 *  @return {[type]}          [description]
 */
const editNode = (property: string, value: any) => {
  // var newValue = JSON.parse(JSON.stringify(value));
  if (property === 'extra') {
    // value is now a plain object (Phase 4), not an Immutable Map.
    const label_en = value?.k?.[0]?.label?.en;
    console.debug('label_en:%O', label_en);
    const id = value?.k?.[0]?.id;
    console.debug('id:%O', id);
  }
  return (dispatch: AppDispatch) => dispatch({ type: EDIT_NODE, payload: { property, value } });
};

/**
 *  [description]
 *  @param  {[type]} picture [description]
 *  @return {[type]}         [description]
 */
const setCustomPicture = (picture: any) => {
  return (dispatch: AppDispatch) => dispatch({ type: SET_CUSTOM_PICTURE, payload: picture });
}

/**
 *  [description]
 *  @param  {[type]} nodeId [description]
 *  @return {[type]}        [description]
 */
const setToggled = (nodeId: string, toggled: boolean) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const currentlyToggled = getState().tree.toggled; // plain array
    const nodeCurrentlyToggled = currentlyToggled.includes(nodeId);
    let updatedToggled = currentlyToggled;

    if (toggled) {
      if (!nodeCurrentlyToggled) {
        updatedToggled = [...currentlyToggled, nodeId];
      }
    } else {
      updatedToggled = currentlyToggled.filter((id: string) => id !== nodeId);
    }

    return dispatch({ type: SET_TOGGLED, payload: updatedToggled });
  }
}

/**
 * Recursive sort of a tree with and array of child trees.
 * @param {[array]} tree [Child tree array of ...]
 * @param {[int]} depth [The safety limit for sorting depth, ref. TREE_SORT_DEPTH]
 * @param {[string]} label [Sorting label]
 */
const sortChildTree = (tree: any, depth: number, label?: string) => {
  // let name = label ? label : 'name';
  // console.log('sortChildTree() - depth : ' + depth);
  if (tree.children && depth-- > 0) {
    for (let i = 0; i < tree.children.length; i++) {
      tree.children[i].children.sort(function (a: any, b: any) {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        return 0;
      });
      sortChildTree(tree.children[i], depth, label);
    };
  };
  return depth;   // this is for reference only
};
//--------------------------------------------------------
// Set window to home and logout
//--------------------------------------------------------
const tokenExpired = (dispatch: AppDispatch, getState: () => RootState) => {
  // Several requests can land here at once (DashboardEditor starts six); only the
  // first should warn and log out — logout() clears isAuthenticated synchronously.
  if (getState().auth.isAuthenticated) {
    toastr.warning('Session expired', 'Please login again');
    // Router navigation, not `window.location.href = '/'`: that tore down the SPA,
    // discarded unsaved editor work, and raced the dispatch below.
    dispatch(logout());
    history.push('/');
  }
  // Never settles: callers may chain `.then` on the thunk's result, and there is
  // nothing for them to do once the session is gone.
  return new Promise<never>(() => {});
};

/**
 *  Fetches target and it's children.
 *  @param  {[type]} target [description]
 *  @return {[type]}        [description]
 */
const fetchTree = (target: any) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {

    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };

    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/target/children`;
    const uid = getState().auth.profile.sub;
    const body = JSON.stringify({
      uid: uid,
      targetId: target,
    });
    console.log('+ fetchTree:%o', url);

    dispatch({ type: REQUEST_TREE });
    return fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: body
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        console.log('+ fetchTree - response:%O', response);
        const tree = response.target;
        tree.children = response.children;
        // Sort left panel selection tree by name.
        if (tree.children) {
          // console.log('*** Sort targets tree ***');
          tree.children.sort(function (a: any, b: any) {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
          });
          sortChildTree(tree, TREE_SORT_DEPTH);
        };

        // Restote tree state.
        const toggled = getState().tree.toggled; // plain array
        const currentNodeId = getState().tree.selectedNode._id;
        prepareTreeState(tree, toggled, currentNodeId);
        console.log('++ fetchTree - tree:%O', tree);
        return dispatch({ type: RECEIVE_TREE_SUCCESS, payload: tree });
      })
      .then(response => dispatch(parseTargets(response.payload)))
      .catch(e => {
        console.error('Error:%O', e);
        dispatch({ type: REQUEST_TREE_ERROR, payload: { errorMessage: e.statusText } });

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
        if (e.status === 400) {
          toastr.warning('Session expired (400)', 'Please login again');
          dispatch(logout());
        }
        if (e.status === 500) {
          toastr.warning('Connection reset (500)', 'Please login again');
          dispatch(logout());
        }
        window.location.href = '/';
      });
    // });
  };
};

/**
 *  [description]
 *  @param  {[type]} targetId [description]
 *  @return {[type]}          [description]
 */
const fetchTarget = (targetId: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {

    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/target/children`;
    const uid = getState().auth.profile.sub;
    const body = JSON.stringify({
      uid: uid,
      // superuserIsActive: superuserIsActive,
      targetId: targetId,
    });
    console.log('===fetchTarget:%o', url);

    dispatch({ type: REQUEST_TARGET });
    return fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: body
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        const target = response.target;
        target.children = response.children;

        // Convert field labels from old string format to new object format.
        const contactExtras = get(target, 'extra.k', []);
        forEach(contactExtras, (contactField, i) => {
          if (typeof contactField === 'string') {
            target.extra.k[i] = {
              id: contactField,
              label: {
                en: contactField
              }
            }
          }

          if (contactField.label && typeof contactField.label === 'string') {
            target.extra.k[i] = {
              en: contactField.label
            };
          }
        });

        return dispatch({ type: RECEIVE_TARGET_SUCCESS, payload: target });
      })
      .then(() => {
        dispatch(parseTargets(getState().tree.tree));
      })
      .catch(e => {
        dispatch({ type: REQUEST_TARGET_ERROR, payload: { errorMessage: e.statusText } });

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
        if (e.status === 500) {
          toastr.warning('Connection reset (500)', 'Please login again');
          dispatch(logout());
        }
      });
    // });
  };
}

/**
 *  [description]
 *  @param  {[type]} targetId [description]
 *  @return {[type]}          [description]
 */
const fetchDashboard = (dashboardId: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/dashboard/dashboard/${dashboardId}`;
    console.log('fetchDashboard:%o', url);

    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestDashboard());
    const uid = getState().auth.profile.sub;
    const data = {
      uid: uid,
      id: dashboardId,
    }
    return fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        return dispatch(requestDashboardSuccess(response));
      })
      .catch(e => {
        dispatch(requestDashboardError({ errorMessage: e.statusText }));

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        } else if (e.status === 404) {
          window.location.href = '/dashboard';
        }
      });
  };
}
//------------------------------------------------------------------------
/**
 *  [description]
 *  @param  {[type]} targetId [description]
 *  @return {[type]}          [description]
 */
const fetchCustomer = (customerId: string | number) => {
  const url = `${TAPLAUSE_NEW_API_BASE_URL}/dashboard/customer?id=${customerId}`;
  console.log('fetchCustomer:%o', url);

  return (dispatch: AppDispatch, getState: () => RootState) => {
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };

    dispatch(requestUserCustomer());
    return fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        return dispatch(requestUserCustomerSuccess(response));
      })
      .catch(e => {
        dispatch(requestUserCustomerError({ errorMessage: e.statusText }));

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        } else if (e.status === 404) {
          window.location.href = '/customer';
        }
      });
  };
}
//------------------------------------------------------------------------
/**
 *  [Fetch dashboard rules from database]
 *  @param  {[string]} dashboardId [dashboard hash]
 *  @return {[json]} [All rules-table items as key:value]
 */
const fetchDashboardRules = (dashboardId: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/dashboard/rules/${dashboardId}`;

    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestDashboardRules());
    const uid = getState().auth.profile.sub;
    const data = {
      uid: uid,
      id: dashboardId,
    }
    return fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        console.info('----- REQUEST_DASHBOARD_RULES_SUCCESS ----- Rules:%O', response);
        return dispatch(requestDashboardRulesSuccess(response));
      })
      .catch(e => {
        dispatch(requestDashboardRulesError({ errorMessage: 'Ihme virhe - ' + e.statusText }));
        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        } else if (e.status === 404) {
          toastr.warning('Dashboard rules not found:', e.statusText);
          window.location.href = '/dashboard';
        }
      });
  };
}

/**
 *  Parses all targets from tree.
 *  @param  {[type]} tree [description]
 *  @return {[type]}      [description]
 */
const parseTargets = (tree: any) => {
  return (dispatch: AppDispatch) => {
    // Was flattenObject(tree) + pickBy(endsWith '_id') + a get() per match, which
    // rebuilt the whole tree as a flat map on every target fetch. A depth-first
    // walk yields the same nodes in the same order.
    const targets = collectTreeNodes(tree).map((t: any) => ({
      _id: t._id,
      name: t.name,
      enabled: t.extra.c.enabled,
      c70: t.extra.c.c70,
    }));

    return dispatch({ type: PARSE_TARGETS, payload: targets });
  };
};

/**
 *  Saves modified target.
 *  @param  {[type]} target [description]
 *  @return {[type]}        [description]
 */
const saveTarget = (target: any, targetRoot: any) => {
  // Remove properties which API won't accept.
  let data = prepareTargetForAPI(target);

  return (dispatch: AppDispatch, getState: () => RootState) => {
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/target/target/${target._id}`;
    const uid = getState().auth.profile.sub;
    data.uid = uid;

    dispatch({ type: SAVE_TARGET });
    return fetch(url, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Save successful', response);
        dispatch({ type: SAVE_TARGET_SUCCESS });
        return dispatch(fetchTree(targetRoot));
      })
      .catch(e => {
        console.error('*** Save target failed:[%O]', e);
        toastr.error('Save failed', e.statusText);
        dispatch({ type: SAVE_TARGET_ERROR, payload: { errorMessage: e.statusText } })

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
      });
    // });
  };
};

/**
 *  [description]
 *  @param  {[type]} dashboard [description]
 *  @return {[type]}           [description]
 */
const saveNewDashboard = (dashboard: any) => {
  // Remove properties which API won't accept.
  const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/dashboard/new`;
  console.log('saveNewDashboard:%o', url);
  const data = prepareDashboardForAPI(dashboard);
  return (dispatch: AppDispatch, getState: () => RootState) => {

    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const uid = getState().auth.profile.sub;
    data.uid = uid;
    dispatch(saveDashboard());
    return fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Save successful');
        dispatch(saveDashboardSuccess());
        return response;
      })
      .catch(e => {
        toastr.error('Save failed', e.statusText);
        dispatch(saveDashboardError({ errorMessage: e.statusText }))

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
      });
  };
};

/**
 *  [description]
 *  @param  {[type]} dashboard [description]
 *  @return {[type]}           [description]
 */
const updateDashboard = (dashboard: any) => {
  const dashboardId = dashboard._id;
  const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/dashboard/dashboard/${dashboardId}`;
  console.log('updateDashboard:%o', url);
  // Remove properties which API won't accept.
  const data = prepareDashboardForAPI(dashboard);
  return (dispatch: AppDispatch, getState: () => RootState) => {

    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const uid = getState().auth.profile.sub;
    data.uid = uid;
    dispatch(saveDashboard());
    return fetch(url, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Save successful');
        dispatch(saveDashboardSuccess());
        return response;
      })
      .catch(e => {
        toastr.error('Save failed', e.statusText);
        dispatch(saveDashboardError({ errorMessage: e.statusText }))

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
      });
  };
};

/**
 *  [description]
 *  @param  {[type]} target [description]
 *  @return {[type]}        [description]
 */
const addTarget = (target: any, targetRoot: any, omitExtraC?: any) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/target/new`;
    console.log('addTarget:%o', url);
    // Remove properties which API won't accept.
    const data = prepareTargetForAPI(target); // target is now a plain object (Phase 4)
    if (omitExtraC) {
      data.extra.c = omit(data.extra.c, omitExtraC);
    };
    dispatch({ type: SAVE_TARGET });
    const uid = getState().auth.profile.sub;
    data.uid = uid;
    return fetch(url,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    )
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Added new target successfully');
        dispatch({ type: SAVE_TARGET_SUCCESS });
        dispatch(fetchTree(targetRoot));
      })
      .catch(e => {
        toastr.error('Adding new target failed failed', e.toString());
        dispatch({ type: SAVE_TARGET_ERROR, payload: { errorMessage: e.toString() } })
      });
  };
};

/**
 *  [description]
 *  @param  {[type]} filtered [description]
 *  @return {[type]}          [description]
 */
const filterTree = (filtered: any) => {
  return (dispatch: AppDispatch) => dispatch({ type: FILTER_TREE, payload: filtered });
};

/**
 *  [description]
 *  @return {[type]} [description]
 */
const resetTree = () => {
  return (dispatch: AppDispatch) => dispatch({ type: RESET_TREE });
};
//------------------------------------------------
// Fetch users by customerId
//------------------------------------------------
const fetchUsers = (customerId: string | number) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/user/getAll`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestUsers());
    return fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ customerId: customerId })
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        if (response) {
          response.sort(function (a: any, b: any) { // by surname
            if (a.name.split(' ').length && b.name.split(' '.length)) {
              if (a.name.split(' ')[1] > b.name.split(' ')[1]) return 1;
              if (a.name.split(' ')[1] < b.name.split(' ')[1]) return -1;
              return 0;
            } else { // by 1st name
              if (a.name > b.name) return 1;
              if (a.name < b.name) return -1;
              return 0;
            }
          });
        };
        console.info('----- REQUEST_USERS_SUCCESS ----- # of users:%O', response.length);
        return dispatch(requestUsersSuccess(response));
      })
      .catch(e => {
        toastr.error('Fetching users fails', e.toString());
        dispatch(requestUsersError({ errorMessage: e.toString() }))
      });
  }
}
//------------------------------------------------
// Fetch user by uid
//------------------------------------------------
const fetchUser = (uid: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/user/get`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestUser());
    return fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ uid: uid })
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        console.info('----- REQUEST_USER_SUCCESS ----- # :%O', response);
        return dispatch(requestUserSuccess(response));
      })
      .catch(e => {
        toastr.error('Fetching users fails', e.toString());
        dispatch(requestUserError({ errorMessage: e.toString() }))
      });
  }
}
//------------------------------------------------
// Create new user to auth0
//------------------------------------------------
const saveNewAuthUser = (data: any) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/auth/new`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    delete data.uid;
    dispatch(requestSaveAuth0User()); // *** TODO AUTH0_USER
    return fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        console.info('----- REQUEST_SAVE_AUTH0_USER_SUCCESS ----- # :%O', response);
        return dispatch(requestSaveAuth0UserSuccess(response));
      })
      .catch(e => {
        toastr.error('Fetching users fails', e.toString());
        dispatch(requestSaveAuth0UserError({ errorMessage: e.toString() }))
      });
  }
}
//------------------------------------------------
// Create new user
//------------------------------------------------
const saveNewUser = (data: any) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/user/new`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    delete data.uid;
    dispatch(requestSaveUser());
    return fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Save successful');
        console.info('----- REQUEST_SAVE_USER_SUCCESS ----- # :%O', response);
        return dispatch(requestSaveUserSuccess(response));
      })
      .catch(e => {
        toastr.error('Saving new user fails', e.toString());
        dispatch(requestSaveUserError({ errorMessage: e.toString() }))
      });
  }
}
//------------------------------------------------
// Update user
//------------------------------------------------
const updateUser = (data: any) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/user/update`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestSaveUser());
    return fetch(url, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Save successful');
        console.info('----- REQUEST_SAVE_USER_SUCCESS ----- # :%O', response);
        return dispatch(requestSaveUserSuccess(response));
      })
      .catch(e => {
        toastr.error('Updating user fails', e.toString());
        dispatch(requestSaveUserError({ errorMessage: e.toString() }))
      });
  }
}

//------------------------------------------------
// Delete target
//------------------------------------------------
const deleteTarget = (id: string, targetRoot: any) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/target/delete`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch({ type: REQUEST_DELETE_TARGET });
    return fetch(url, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ target_hashes: [id] })
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            const error = new Error(errorText || response.statusText || 'An error occurred');
            (error as any).status = response.status;
            throw error;
          });
        }
        return response.json();
      })
      .then(response => {
        toastr.success('Delete successful');
        console.info('----- REQUEST_DELETE_TARGET_SUCCESS ----- # :%O', response);
        dispatch({ type: REQUEST_DELETE_TARGET_SUCCESS, payload: response });
        return dispatch(fetchTree(targetRoot));
      })
      .catch(e => {
        toastr.error('Deleting target failed', e.message);
        dispatch({ type: REQUEST_DELETE_TARGET_ERROR, payload: { errorMessage: e.message } })
      });
  }
}

//------------------------------------------------
// Delete target feedback
//------------------------------------------------
const deleteTargetFeedback = (id: string, targetRoot: any) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/target/delete-feedback`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch({ type: REQUEST_DELETE_TARGET_FEEDBACK });
    return fetch(url, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ target_hashes: [id] })
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            const error = new Error(errorText || response.statusText || 'An error occurred');
            (error as any).status = response.status;
            throw error;
          });
        }
        return response.json();
      })
      .then(response => {
        toastr.success('Feedback deleted successfully');
        console.info('----- REQUEST_DELETE_TARGET_FEEDBACK_SUCCESS ----- # :%O', response);
        return dispatch({ type: REQUEST_DELETE_TARGET_FEEDBACK_SUCCESS, payload: response });
      })
      .catch(e => {
        toastr.error('Deleting target feedback failed', e.message);
        dispatch({ type: REQUEST_DELETE_TARGET_FEEDBACK_ERROR, payload: { errorMessage: e.message } })
      });
  }
}

//------------------------------------------------
// Delete dashboard
//------------------------------------------------
const deleteDashboard = (hash: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/dashboard/delete`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestDeleteDashboard());
    return fetch(url, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ dashboard_hashes: [hash] })
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Delete successful');
        console.info('----- REQUEST_DELETE_DASHBOARD_SUCCESS ----- # :%O', response);
        return dispatch(requestDeleteDashboardSuccess(response));
      })
      .catch(e => {
        toastr.error('Deleting dashboard failed');
        dispatch(requestDeleteDashboardError({ errorMessage: e.toString() }))
      });
  }
}

//------------------------------------------------
// Delete user
//------------------------------------------------
const deleteUser = (uid: string, disabled: boolean) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/user/delete`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestDeleteUser());
    return fetch(url, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ uids_to_delete: [uid] })
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Delete successful');
        console.info('----- REQUEST_DELETE_USER_SUCCESS ----- # :%O', response);
        return dispatch(requestSaveUserSuccess(response));
      })
      .catch(e => {
        toastr.error('Deleting user failed', e.toString());
        dispatch(requestDeleteUserError({ errorMessage: e.toString() }))
      });
  }
}

//------------------------------------------------
// Delete customer
//------------------------------------------------
const deleteCustomer = (customerName: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_BASE_URL}/dashboard/customer/delete`;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestDeleteCustomer());
    return fetch(url, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ customer_names: [customerName] })
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            const error = new Error(errorText || response.statusText || 'An error occurred');
            (error as any).status = response.status;
            throw error;
          });
        }
        return response.json();
      })
      .then(response => {
        toastr.success('Delete successful');
        console.info('----- REQUEST_DELETE_CUSTOMER_SUCCESS ----- # :%O', response);
        return dispatch(requestDeleteCustomerSuccess());
      })
      .catch(error => {
        toastr.error('Deleting customer failed', error.message);
        dispatch(requestDeleteCustomerError({ errorMessage: error.message }))
      });
  }
}


const validateUser = () => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const name = getState().user.userName;
    const email = getState().user.email;
    const password = getState().user.password;
    const userId = getState().user.uid;
    const customerId = getState().user.customerId;
    const customerName = getState().user.customerName;
    console.log('password:%o, userId:%o, customerId:%o, customerName:%o', password, userId, customerId, customerName);
    // The API rejects a user without both (422).
    const isUserValid = name.length > 0 && MAIL_REGEXP.test(email) && !!customerId && customerName.length > 0 && (typeof password === 'undefined' || password !== '' && password.length >= PASSWORD_MIN_LENGTH);
    dispatch(validateUserAction(isUserValid));
    return isUserValid
  };
};
/**
 *  [description]
 *  @return {[type]} [description]
 */
const resetUser = () => {
  return (dispatch: AppDispatch) => dispatch(resetUserAction());
};
/**
 *  [description]
 *  @param  {[boolean]} showDisabledUsers [description]
 *  @return {[type]}              [description]
 */
const editUser = (property: string, value: unknown) => {
  return (dispatch: AppDispatch) => {
    dispatch(editUserAction({
        property: property,
        value: value
      }))
    dispatch(validateUser());
  }
}

/**
 *  [description]
 *  @param  {[type]} targetId [description]
 *  @return {[type]}          [description]
 */
const fetchUserTargets = () => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    // const token = getState().auth.token;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/target/names`;
    const uid = getState().auth.profile.sub;
    var targets: any[] = []; // localStorage.getItem(USER_TARGETS_KEY); // getState().auth.getIn(['profile', 'targets'], []);
    /*
    if (targets.length > 0) {
      targets = targets.split(',');
      console.log('    actionCreators, fetchUserTargets - targets[' + typeof(targets) + ']: ' + JSON.stringify(targets));
    } else {
      console.log('    actionCreators, fetchUserTargets - New interface: targets empty, NOT FIXED.');
      // targets = ['BJ6OcZN7'];
    };
    */
    dispatch(requestUserTargets());
    return fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        targets: targets,
        uid: uid,
      })
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        // console.log('***A actionCreators, fetchUserTargets - SAVE response[' + typeof(response) + ']: ' + JSON.stringify(response));
        var len = response.targets.length;
        if (len > 0) {
          for (var i = 0; i < len; i++) {
            // console.log('[' + i + ']:' + response[i]["_id"]);
            targets[i] = response.targets[i]["_id"];
          };
          localStorage.setItem(USER_TARGETS_KEY, String(targets));
          // targets = localStorage.getItem(USER_TARGETS_KEY);
          // console.log('    actionCreators, fetchUserTargets - GET SAVED [' + typeof(targets) + ']: ' + JSON.stringify(targets));
        }
        return dispatch(requestUserTargetsSuccess(response));
      })
      .catch(e => {
        dispatch(requestUserTargetsError({ errorMessage: e.statusText }));

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
      });
    // });
  };
};

/**
 *  [description]
 *  @param  {[type]} customerId [description]
 *  @return {[type]}          [description]
 */
const fetchUserTargetsRoot = (customerId: string | number) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    // const token = getState().auth.token;
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/target/root`;
    const uid = getState().auth.profile.sub;
    dispatch(requestUserTargetsRoot());
    return fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        customerId: customerId,
        uid: uid,
      })
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        return dispatch(requestUserTargetsRootSuccess(response));
      })
      .catch(e => {
        dispatch(requestUserTargetsRootError({ errorMessage: e.statusText }));
        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
      });
  };
};
/**
 *  [description]
 *  @param  {[type]} targetId [description]
 *  @return {[type]}          [description]
 */
const toggleSelectedRoot = (targetId: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const currentTargets = getState().user.selectedTargets;
    const index = currentTargets.findIndex(id => id === targetId);
    // plain array now (M5): filter to remove, spread to add
    const targets = index > -1 ? currentTargets.filter(id => id !== targetId) : [...currentTargets, targetId];

    dispatch(setSelectedRootTargets(targets));
  }
};
/**
 *  [description]
 *  @param  {[type]} targetId [description]
 *  @return {[type]}          [description]
 */
const toggleSelectedDashboard = (targetId: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const currentDashboards = getState().user.selectedDashboards;
    const index = currentDashboards.findIndex(id => id === targetId);
    // plain array now (M5): filter to remove, spread to add
    const dashboards = index > -1 ? currentDashboards.filter(id => id !== targetId) : [...currentDashboards, targetId];

    dispatch(setSelectedDashboards(dashboards));
  }
};
/**
 * Redirect from http to https-connection
 *
 * @param {[none]} none [No parameters]
 * @return {[none]} none [No set return value]
 *
 */
const confirmRedirect = () => {
  const url = window.location.href;
  console.log('OK: clicked:%O', url);
  if (url.startsWith('http://')) {
    const newUrl = url.replace(/http/g, 'https');
    console.log('Unsecure, fixed:%O', newUrl);
    window.location.href = newUrl;
  } else {
    console.error('Something really wrong.');
  };
};

/**
 * Dashboard editor, fetch all user dashboards.
 *
 *  @param  {[type]} targetId [description]
 *  @return {[type]}          [description]
 */
const fetchUserDashboards = (customerId?: string | number) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const isSuperuserActive = localStorage.getItem(SUPERUSER_ACTIVE_KEY);
    console.log('+++ActionCreators - SuperuserIsActive:%O', isSuperuserActive);

    const uid = getState().auth.profile.sub;

    dispatch(requestUserDashboards());
    return fetch(`${TAPLAUSE_NEW_API_ADMIN_BASE}/dashboard/dashboards`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        uid: uid,
        superuserIsActive: isSuperuserActive,
        customerId: customerId,
      })
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        if (response.length > 0) {
          // console.log('response: ' + JSON.stringify(response[0]));
          // console.log('    actionCreators, fetchUserDashboards - Sort dashboards.');
          response.sort(function (a: any, b: any) {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
          }
          );
        } else {
          console.log('actionCreators, fetchUserDashboards - No dashboards.');
        };
        return dispatch(requestUserDashboardsSuccess(response));
      })
      .catch(e => {
        dispatch(requestUserDashboardsError({ errorMessage: e.message }));
        // Logout if "Bad HTTP authentication header format"
        console.error('Error:%O', e.message);
        if (sessionExpired(e)) {
          dispatch(logout());
        } else {
          const toastrConfirmOptions = {
            onOk: () => confirmRedirect(),
            onCancel: () => console.log('CANCEL: clicked'),
            okText: 'Ok [Secure]',
            cancelText: 'Cancel'
          };
          const url = window.location.href;
          if (url.startsWith('http://')) {
            const message = 'Fetching dashboards failed. You need secure connection.';
            toastr.confirm(message, toastrConfirmOptions);
          } else {
            toastr.error('Fetching dashboards failed', 'Try again later');
            dispatch(logout());
          };
        };
      });
    // });
  };
};
//----------------------------------------------
/**
 * Fetch all user customers.
 *
 *  @param  {[type]} targetId [description]
 *  @return {[type]}          [description]
 */
const fetchUserCustomers = () => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = `${TAPLAUSE_NEW_API_BASE_URL}/dashboard/customer/all`;
    console.log('>>> actionCreators, fetchUserCustomers:', url);
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };

    dispatch(requestUserCustomers());
    return fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        if (response.length > 0) {
          // console.log('response: ' + JSON.stringify(response));
          // console.log('    actionCreators, fetchUserCustomer - Sort dashboards.');
          response.sort(function (a: any, b: any) {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
          }
          );
        } else {
          console.log('actionCreators, fetchUserCustomers - No customers.');
        };
        return dispatch(requestUserCustomersSuccess(response));
      })
      .catch(e => {
        dispatch(requestUserCustomersError({ errorMessage: e.message }));
        // Logout if "Bad HTTP authentication header format"
        console.error('Error:%O', e.message);
        if (sessionExpired(e)) {
          dispatch(logout());
        } else {
          const toastrConfirmOptions = {
            onOk: () => confirmRedirect(),
            onCancel: () => console.log('CANCEL: clicked'),
            okText: 'Ok [Secure]',
            cancelText: 'Cancel'
          };
          const currentURL = window.location.href;
          if (currentURL.startsWith('http://')) {
            const message = 'Fetching customers failed. You need secure connection.';
            toastr.confirm(message, toastrConfirmOptions);
          } else {
            toastr.error('Fetching customers failed', 'Try again later');
            dispatch(logout());
          };
        };
      });
  };
};

/**
 *  [description]
 *  @param  {[type]} customer [description]
 *  @return {[type]}           [description]
 */
const saveNewCustomer = (customer: any) => {
  const url = `${TAPLAUSE_NEW_API_BASE_URL}/dashboard/customer`;
  console.log('saveNewCustomer:%o', url);
  const data = {
    customer: {
      name: customer.name,
      email: customer.email,
      disabled: customer.disabled,
      themes: customer.themes,
    },
  };
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestSaveUserCustomer());
    return fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Save successful');
        dispatch(requestSaveUserCustomerSuccess());
        return response;
      })
      .catch(e => {
        toastr.error('Save failed', e.statusText);
        dispatch(requestSaveUserCustomerError({ errorMessage: e.statusText }))

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
      });
  };
};

/**
 *  [description]
 *  @param  {[type]} customer [description]
 *  @return {[type]}           [description]
 */
const updateCustomer = (customer: any) => {
  const url = `${TAPLAUSE_NEW_API_BASE_URL}/dashboard/customer/${customer.id}`;
  console.log('updateCustomer:%o', url);
  const data = {
    customer: {
      id: customer.id,
      name: customer.name,
      email: customer.email,
      disabled: customer.disabled
    },
  };
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    dispatch(requestSaveUserCustomer());
    return fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        toastr.success('Save successful');
        dispatch(requestSaveUserCustomerSuccess());
        return response;
      })
      .catch(e => {
        toastr.error('Save failed', e.statusText);
        dispatch(requestSaveUserCustomerError({ errorMessage: e.statusText }))

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
      });
  };
};

/**
 *  [description]
 *  @param  {[type]} id [Customer id or '*' for all themes]
 *  @return {[type]}           [description]
 */
const fetchThemes = (id: string | number) => {
  const url = `${TAPLAUSE_NEW_API_BASE_URL}/admin/themes/config`;
  console.log('fetchThemes:%o', url);
  const data = {
    id: id,
  };
  // The `themes` store slice was write-only (nothing read it), so it was removed;
  // this thunk is kept for its RETURN VALUE — initTheme (DashboardEditor) builds
  // the theme dropdown from the resolved response, not from redux.
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    return fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(handleErrors)
      .then(response => response.json())
      .catch(e => {
        toastr.error('Themes fetch failed', e.statusText);

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
      });
  };
};
//------------------------------------------------------------------------
// Dashboard editor, fetch 'Available targets' for a user.
// - returns an array of targets
//------------------------------------------------------------------------
const fetchDashboardTargets = (customerId: string | number) => {
  // console.log('>>> fetchDashboardTargets');
  return (dispatch: AppDispatch, getState: () => RootState) => {

    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/target/children`;
    const uid = getState().auth.profile.sub;

    let targets: any = localStorage.getItem(USER_TARGETS_KEY);

    if (targets) {
      targets = targets.split(',');
    } else {
      console.log('fetchDashboardTargets - New interface. Dashboard targets empty, NOT FIXED.');
    };


    dispatch(requestDashboardTargets());
    const requests = targets.map((target: any) => {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uid: uid,
          // superuserIsActive: superuserIsActive,
          targetId: target,
          // customerId: customerId,
        })
      })
        .then(handleErrors)
        .then(response => response.json())
        .then(response => {
          const user = response.user;
          const tree = response.target;
          tree.children = response.children;
          if (tree.children) {
            // console.log('     fetchDashboardTargets - Sort targets tree.');
            tree.children.sort(function (a: any, b: any) {
              if (a.name > b.name) return 1;
              if (a.name < b.name) return -1;
              return 0;
            });
            sortChildTree(tree, TREE_SORT_DEPTH);
          } else {
            console.log('fetchDashboardTargets - No childen.');
          };
          tree.user = user;
          // console.log('+++ tree:%O', tree)
          return tree;
        });
    });
    Promise.all(requests)
      .then(response => {
        return dispatch(requestDashboardTargetsSuccess(response));
      })
      .catch(e => {
        dispatch(requestDashboardTargetsError({ errorMessage: e.errorMessage }));

        // Logout if token was expired.
        if (e.status === 401 && e.headers.get('Www-Authenticate') === ERROR_TOKEN_EXPIRED) {
          toastr.warning('Session expired', 'Please login again');
          dispatch(logout());
        }
        if (e.status === 500) {
          toastr.warning('Connection reset (500)', 'Please login again');
          dispatch(logout());
        }
      });
    // });
  }
};

/**
 *  [description]
 *  @param  {[type]} targetId [description]
 *  @return {[type]}          [description]
 */
const toggleSelectedTarget = (targetId: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const currentTargets = getState().dashboard.selectedTargets;
    const index = currentTargets.findIndex(id => id === targetId);
    // plain array now (M5): filter to remove, spread to add
    const targets = index > -1 ? currentTargets.filter(id => id !== targetId) : [...currentTargets, targetId];

    dispatch(setSelectedTargets(targets));
    dispatch(validateDashboard());
  }
};

/**
 *  [description]
 *  @param  {[type]} property [description]
 *  @param  {[type]} value    [description]
 *  @return {[type]}          [description]
 */
const editDashboard = (property: string, value: unknown) => {
  return (dispatch: AppDispatch) => {
    dispatch(editDashboardAction({
        property: property,
        value: value
      }));
    dispatch(validateDashboard());
  }
};

const validateDashboard = () => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const name = getState().dashboard.name;
    const email = getState().dashboard.email;
    const selectedTargets = getState().dashboard.selectedTargets;

    const isDashboardValid = name.length > 0 &&
      MAIL_REGEXP.test(email) &&
      selectedTargets.length > 0; // plain array now (M5)
    dispatch(validateDashboardAction(isDashboardValid));
    return isDashboardValid
  };
};

/**
 *  [description]
 *  @return {[type]} [description]
 */
const resetDashboard = () => {
  return (dispatch: AppDispatch) => dispatch(resetDashboardAction());
};


/**
 *  [description]
 *  @param  {[type]} property [description]
 *  @param  {[type]} value    [description]
 *  @return {[type]}          [description]
 */
const editCustomer = (property: string, value: unknown, novalidate?: boolean) => {
  return (dispatch: AppDispatch) => {
    dispatch(requestEditUserCustomer({
        property: property,
        value: value
      }));
    if (!novalidate) {
      dispatch(validateCustomer());
    }
  }
};

const validateCustomer = () => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const name = getState().customer.customerName;
    const email = getState().customer.email;
    console.log('customerName:%O, email:%o', name, email);

    const isCustomerValid = (name ?? '').length > 0 && MAIL_REGEXP.test(email);

    dispatch(requestValidateUserCustomer(isCustomerValid));
  };
};
/**
 *  [description]
 *  @return {[type]} [description]
 */
const resetCustomer = () => {
  return (dispatch: AppDispatch) => dispatch(resetCustomerAction());
};

/**
 *  [description]
 *  @param  {[type]} name [description]
 *  @param  {[type]} type [description]
 *  @return {[type]}      [description]
 */
const signS3Upload = (name: string, type: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const token = getToken();
    if (!token) {
      return tokenExpired(dispatch, getState);
    };
    const url = `${TAPLAUSE_NEW_API_ADMIN_BASE}/aws/sign_s3`;
    const uid = getState().auth.profile.sub;

    dispatch({ type: REQUEST_S3_SIGNATURE });
    return fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        uid: uid,
        filename: name,
        filetype: type
      })
    })
      .then(handleErrors)
      .then(response => response.json())
      .then(response => {
        return dispatch({ type: RECEIVE_S3_SIGNATURE_SUCCESS, payload: response });
      })
      .catch(e => {
        dispatch({ type: REQUEST_S3_SIGNATURE_ERROR, payload: { errorMessage: e.statusText } });
      });
    // });
  };
}

/**
 *  [description]
 *  @param  {[type]} picture   [description]
 *  @param  {[type]} signature [description]
 *  @return {[type]}           [description]
 */
const s3Upload = (picture: any, signature: any) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const url = signature.signedRequest;
    dispatch({ type: REQUEST_S3_UPLOAD });
    return fetch(url, {
      method: 'PUT',
      headers: {
        'x-amz-acl': 'public-read',
        'Content-Type': picture.type
      },
      body: picture
    })
      .then(handleErrors)
      .then(response => {
        return dispatch({ type: RECEIVE_S3_UPLOAD_SUCCESS, payload: signature.url });
      })
      .catch(e => {
        dispatch({ type: REQUEST_S3_UPLOAD_ERROR, payload: { errorMessage: e.statusText } });
      });
  };
}

// /**
//  *  Upload through taplause-api
//  *  @param  {[type]} picture  [description]
//  *  @param  {[type]} filetype [description]
//  *  @return {[type]}          [description]
//  */
// const s3Upload = (picture, filetype) => {
//   return (dispatch: AppDispatch, getState: () => RootState) => {
//
//     dispatch({ type: REQUEST_S3_UPLOAD });
//     return fetch(`${API_URL}/upload_s3`, {
//       method: 'PUT',
//       headers: {
//         'Accept': 'application/json',
//         'Content-Type': 'application/json'
//       },
//       body: JSON.stringify({
//         body: picture,
//         filetype: filetype
//       })
//     })
//     .then(handleErrors)
//     .then(response => response.json())
//     .then(response => {
//       return dispatch({ type: RECEIVE_S3_UPLOAD_SUCCESS, payload: response });
//     })
//     .catch(e => {
//       dispatch({ type: REQUEST_S3_UPLOAD_ERROR, payload: { errorMessage: e.statusText }});
//     });
//   };
// }

export {
  selectNode,
  deselectNode,
  toggleNode,
  showDisabledTargets,
  setSuperuserActive,
  changeParent,
  editNode,
  setCustomPicture,
  setToggled,
  fetchTarget,
  fetchTree,
  fetchDashboard,
  parseTargets,
  saveTarget,
  saveNewDashboard,
  updateDashboard,
  addTarget,
  filterTree,
  resetTree,
  login,
  logout,
  logoutFederated,
  fetchUserTargets,
  fetchUserTargetsRoot,
  fetchUserDashboards,
  fetchUserCustomers,
  fetchCustomer,
  fetchThemes,
  fetchDashboardTargets,
  toggleSelectedTarget,
  editDashboard,
  validateDashboard,
  resetDashboard,
  editCustomer,
  validateCustomer,
  resetCustomer,
  saveNewCustomer,
  updateCustomer,
  signS3Upload,
  s3Upload,
  fetchDashboardRules,
  fetchUsers,
  fetchUser,
  editUser,
  resetUser,
  updateUser,
  saveNewUser,
  saveNewAuthUser,
  deleteTarget,
  deleteTargetFeedback,
  deleteDashboard,
  deleteUser,
  deleteCustomer,
  toggleSelectedRoot,
  toggleSelectedDashboard,
};
